// In this file, all Page components from 'src/pages` are auto-imported. Nested
// directories are supported, and should be uppercase. Each subdirectory will be
// prepended onto the component name.
//
// Examples:
//
// 'src/pages/HomePage/HomePage.js'         -> HomePage
// 'src/pages/Admin/BooksPage/BooksPage.js' -> AdminBooksPage

import { UserRole } from '@api/db/enums'

import { PrivateSet, Route, Router, Set } from '@cedarjs/router'
import { ParamType } from '@cedarjs/router/util'

import { useAuth } from 'src/auth'
import MainLayout from 'src/layouts/MainLayout/MainLayout'

import Loader from './components/Custom/Loader/Loader'
import { MetadataGenresPage, MetadataVibesPage } from './pages/GlobalMetaDataManagerPage/Components/MetadataListingTables'
import MissingMetadatListing from './pages/GlobalMetaDataManagerPage/Components/MissingMetadatListing'
import NewShowsListing from './pages/GlobalMetaDataManagerPage/Components/NewShowsListing'
import GlobalStationManagerPage from './pages/GlobalStationManagerPage/GlobalStationManagerPage'
import GlobalUsersPage from './pages/GlobalUsersPage/GlobalUsersPage'
import AudiostreamManagerPage from './pages/LocalStationManager/Audiostreams/AudiostreamManagerPage'
import StationConfigurationPage from './pages/LocalStationManager/Config/StationConfigurationPage'
import LivestreamManagerPage from './pages/LocalStationManager/Live/LivestreamManagerPage'
import LocalStationManager from './pages/LocalStationManager/LocalStationManager'
import UserConfigurationPage from './pages/LocalStationManager/Users/UserConfigurationPage'
import StationPresentingSponsorsPage from './pages/PresentingSponsor/StationPresentingSponsorsPage/StationPresentingSponsorsPage'
import SystemMessagingPage from './pages/SystemMessagingPage/SystemMessagingPage'

const customRouteParamTypes: Record<string, ParamType> = {
  // Station call sign: exactly 4 upper case letters starting with K or W.
  stationCallSign: {
    match: /[KW]{1}[A-Z]{3}/,
  },
  uuid: {
    match: /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/,
  },
}

// See: https://community.redwoodjs.com/t/prerendering-whileloadingauth-and-unexpected-jsx-element-in-routes-tsx/5924
// eslint-disable-next-line
const PageLoader = () => <div className="absolute left-1/2 top-1/2"><Loader /></div>
// eslint-disable-next-line
const MainLoader = () => <MainLayout loading />

// See API documentation on `UserRole` enum for role access guidance.
const Routes = () => {
  return (
    <Router useAuth={useAuth} paramTypes={customRouteParamTypes}>
      <Route path="/" page={HomePage} whileLoadingPage={PageLoader} name="home" />

      <Set>
        <Route path="/login" page={LoginPage} name="login" />
        <Route path="/forgot-password" page={ForgotPasswordPage} name="forgotPassword" />
        <Route path="/reset-password" page={ResetPasswordPage} name="resetPassword" />
      </Set>

      <Set>
        <Route path="/activate-device/{stationCallSign:stationCallSign}" page={ActivateDevicePage} name="activateDevice" />
        <Route path="/device-login/{stationCallSign:stationCallSign}" page={DeviceLoginPage} name="deviceLogin" />
      </Set>

      <PrivateSet unauthenticated="login" whileLoadingAuth={MainLoader} wrap={MainLayout}>
        <Route path="/dashboard" page={DashboardPage} name="dashboard" />
      </PrivateSet>

      <PrivateSet unauthenticated="forbidden" roles={[UserRole.STATION_PROGRAMMER]} whileLoadingAuth={MainLoader} wrap={MainLayout}>
        <Route path="/carousels/lineup" page={CarouselLineupPage} name="carouselLineup" />
        <Route path="/heroes/lineup" page={HeroLineupPage} name="heroLineup" />
        <Route path="/shows" page={ShowShowsPage} name="shows" />
        <Route path="/shows/{id:uuid}/edit" page={ShowEditShowPage} name="editShow" />
        <Route path="/videos" page={VideoVideosPage} name="videos" />
        <Route path="/videos/{id:uuid}/edit" page={VideoEditVideoPage} name="editVideo" />
      </PrivateSet>

      <PrivateSet unauthenticated="forbidden" roles={[UserRole.STATION_MARKETER]} whileLoadingAuth={MainLoader} wrap={MainLayout}>
        <Route path="/messaging" page={MemberMessagingPage} name="messaging" />
        <Route path="/presenting-sponsors" page={StationPresentingSponsorsPage} name="presentingSponsors" />
        <Route path="/presenting-sponsors/{id:uuid}/edit" page={PresentingSponsorEditPresentingSponsorPage} name="editPresentingSponsor" />
        <Route path="/presenting-sponsors/new" page={PresentingSponsorNewPresentingSponsorPage} name="newPresentingSponsor" />
        <Route path="/promo-messages/{id:uuid}/edit" page={PromoMessageEditPromoMessagePage} name="editPromoMessage" />
        <Route path="/promo-messages/new" page={PromoMessagePromoMessagesPage} name="newPromoMessage" />
        <Route path="/promos/{id:uuid}/edit" page={PromoEditPromoPage} name="editPromo" />
        <Route path="/promos/new" page={PromoNewPromoPage} name="newPromo" />
      </PrivateSet>

      <PrivateSet unauthenticated="forbidden" roles={[UserRole.STATION_GM]} whileLoadingAuth={MainLoader} wrap={MainLayout}>
        <Route path="/station" page={LocalStationManager} name="localStationManager" />
        <Route path="/station/livestreams" page={LivestreamManagerPage} name="livestreamManagerPage" />
        <Route path="/station/audiostreams" page={AudiostreamManagerPage} name="audiostreamManagerPage" />
        <Route path="/station/users" page={UserConfigurationPage} name="userConfigurationPage" />
      </PrivateSet>

      <PrivateSet unauthenticated="forbidden" roles={[UserRole.ADMIN, UserRole.GLOBAL_PROGRAMMER]} whileLoadingAuth={MainLoader} wrap={MainLayout}>
        <Route path="/global/carousel-lineups" page={CollectionGroupCollectionGroupsPage} name="collectionGroups" />
        <Route path="/global/carousel-lineups/{id:uuid}/edit" page={CollectionGroupEditCollectionGroupPage} name="editCollectionGroup" />
        <Route path="/global/carousel-lineups/new" page={CollectionGroupNewCollectionGroupPage} name="newCollectionGroup" />
        <Route path="/global/carousels/lineup/{id:uuid}/edit" page={CarouselLineupPage} name="editCarouselLineup" />
        <Route path="/global/genres" page={MetadataGenresPage} name="globalMetaDataManagerGenres" />
        <Route path="/global/genres/{id:uuid}/edit" page={GenreVibeEditGenreVibePage} name="editGenre" />
        <Route path="/global/genres/new" page={GenreVibeNewGenreVibePage} name="newGenre" />
        <Route path="/global/hero-lineups" page={HeroGroupHeroGroupsPage} name="heroGroups" />
        <Route path="/global/hero-lineups/{id:uuid}/edit" page={HeroGroupEditHeroGroupPage} name="editHeroGroup" />
        <Route path="/global/hero-lineups/new" page={HeroGroupNewHeroGroupPage} name="newHeroGroup" />
        <Route path="/global/heroes/lineup/{id:uuid}/edit" page={HeroLineupPage} name="editHeroLineup" />
        <Route path="/global/shows" page={ShowShowsPage} name="globalShows" />
        <Route path="/global/shows/{id:uuid}/edit" page={ShowEditShowPage} name="editGlobalShow" />
        <Route path="/global/shows/added" page={NewShowsListing} name="globalMetaDataManagerNewShows" />
        <Route path="/global/shows/missing-metadata" page={MissingMetadatListing} name="globalMetaDataManagerListing" />
        <Route path="/global/vibes" page={MetadataVibesPage} name="globalMetaDataManagerVibes" />
        <Route path="/global/vibes/{id:uuid}/edit" page={GenreVibeEditGenreVibePage} name="editVibe" />
        <Route path="/global/vibes/new" page={GenreVibeNewGenreVibePage} name="newVibe" />
        <Route path="/global/videos" page={VideoVideosPage} name="globalVideos" />
        <Route path="/global/videos/{id:uuid}/edit" page={VideoEditVideoPage} name="editGlobalVideo" />
      </PrivateSet>

      <PrivateSet unauthenticated="forbidden" roles={[UserRole.ADMIN, UserRole.GLOBAL_GM]} whileLoadingAuth={MainLoader} wrap={MainLayout}>
        <Route path="/global/stations" page={GlobalStationManagerPage} name="globalStationManager" />
        <Route path="/global/stations/{id:uuid}" page={LocalStationManager} name="editLocalStation" />
        <Route path="/global/stations/{id:uuid}/livestreams" page={LivestreamManagerPage} name="editLocalStationLivestreams" />
        <Route path="/global/stations/{id:uuid}/audiostreams" page={AudiostreamManagerPage} name="editLocalStationAudiostreams" />
        <Route path="/global/stations/{id:uuid}/users" page={UserConfigurationPage} name="editLocalStationUsers" />
        <Route path="/global/stream-schedules/new" page={StreamScheduleNewStreamSchedulePage} name="newStreamSchedule" />
        <Route path="/global/stream-schedules/{id:uuid}/edit" page={StreamScheduleEditStreamSchedulePage} name="editStreamSchedule" />
        <Route path="/global/stream-schedules" page={StreamScheduleStreamSchedulesPage} name="streamSchedules" />
        <Route path="/global/system-messaging" page={SystemMessagingPage} name="systemMessaging" />
        <Route path="/global/users" page={GlobalUsersPage} name="globalUsers" />
      </PrivateSet>

      <PrivateSet unauthenticated="forbidden" roles={[UserRole.ADMIN]} whileLoadingAuth={MainLoader} wrap={MainLayout}>
        <Route path="/api-users" page={ApiUserApiUsersPage} name="apiUsers" />
        <Route path="/api-users/{id:uuid}/edit" page={ApiUserEditApiUserPage} name="editApiUser" />
        <Route path="/api-users/new" page={ApiUserNewApiUserPage} name="newApiUser" />
        <Route path="/global/stations/{id:uuid}/config" page={StationConfigurationPage} name="editStationConfiguration" />
        <Route path="/pbs-account-configs" page={PbsAccountConfigPbsAccountConfigsPage} name="pbsAccountConfigs" />
        <Route path="/pbs-account-configs/{id:uuid}/edit" page={PbsAccountConfigEditPbsAccountConfigPage} name="editPbsAccountConfig" />
        <Route path="/pbs-account-configs/new" page={PbsAccountConfigNewPbsAccountConfigPage} name="newPbsAccountConfig" />
        <Route path="/webhooks-users" page={WebhooksUserWebhooksUsersPage} name="webhooksUsers" />
        <Route path="/webhooks-users/{id:uuid}/edit" page={WebhooksUserEditWebhooksUserPage} name="editWebhooksUser" />
        <Route path="/webhooks-users/new" page={WebhooksUserNewWebhooksUserPage} name="newWebhooksUser" />
        <Route path="/station-credential-sets" page={StationCredentialSetStationCredentialSetsPage} name="stationCredentialSets" />
        <Route path="/station-credential-sets/{id:uuid}/edit" page={StationCredentialSetEditStationCredentialSetPage} name="editStationCredentialSet" />
        <Route path="/station-credential-sets/new" page={StationCredentialSetNewStationCredentialSetPage} name="newStationCredentialSet" />
        <Route path="/webhooks" page={WebhookWebhooksPage} name="webhooks" />
        <Route path="/webhooks/{id:uuid}/edit" page={WebhookEditWebhookPage} name="editWebhook" />
        <Route path="/webhooks/new" page={WebhookNewWebhookPage} name="newWebhook" />
      </PrivateSet>

      <Route notfound page={NotFoundPage} />
      <Route path="/forbidden" page={ForbiddenPage} name="forbidden" />
    </Router>
  )
}

export default Routes
